import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { formatDateShort } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Bell,
  BellOff,
  Loader2,
  Briefcase,
  Calendar,
  XCircle,
  MessageSquare,
  ClipboardList,
  CheckCheck,
  Trash2,
  ChevronRight,
  Home
} from 'lucide-react';

const MobileNotifications = () => {
  const navigate = useNavigate();
  const { api, tenantSettings } = useMobileAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/api/mobile/notifications?limit=50');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/api/mobile/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/api/mobile/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/api/mobile/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'job_assigned':
        return <Briefcase className="h-5 w-5 text-green-400" />;
      case 'job_changed':
        return <Calendar className="h-5 w-5 text-yellow-400" />;
      case 'job_cancelled':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'message':
        return <MessageSquare className="h-5 w-5 text-blue-400" />;
      case 'daily_summary':
        return <ClipboardList className="h-5 w-5 text-purple-400" />;
      default:
        return <Bell className="h-5 w-5 text-[var(--m-text-secondary)]" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'job_assigned': return 'New Job';
      case 'job_changed': return 'Schedule Change';
      case 'job_cancelled': return 'Cancelled';
      case 'message': return 'Message';
      case 'daily_summary': return 'Summary';
      default: return 'Notification';
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'job_assigned': return 'bg-green-900 text-green-300 hover:bg-green-800';
      case 'job_changed': return 'bg-yellow-900 text-yellow-300 hover:bg-yellow-800';
      case 'job_cancelled': return 'bg-red-900 text-red-300 hover:bg-red-800';
      case 'message': return 'bg-blue-900 text-blue-300 hover:bg-blue-800';
      case 'daily_summary': return 'bg-purple-900 text-purple-300 hover:bg-purple-800';
      default: return 'bg-[var(--m-bg-elevated)] text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)]/80';
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return mins <= 1 ? 'Just now' : `${mins}m ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return days === 1 ? 'Yesterday' : `${days}d ago`;
    }
    // Older
    return formatDateShort(isoString, tenantSettings?.date_format);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Navigate to job if job_id exists
    if (notification.job_id) {
      navigate(`/mobile/jobs/${notification.job_id}?date=today`);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-[var(--m-text)] font-bold flex items-center gap-2">
                <Bell className="h-5 w-5 text-[#ffd108]" />
                Notifications
              </h1>
              {unreadCount > 0 && (
                <p className="text-[var(--m-text-secondary)] text-xs">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                className="text-[#ffd108] text-xs"
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/dashboard')}
              className="text-[var(--m-text-secondary)] p-2"
              data-testid="home-btn"
            >
              <Home className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : notifications.length === 0 ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="py-8 text-center">
              <BellOff className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-[var(--m-text-secondary)]">No notifications</p>
              <p className="text-[var(--m-text-muted)] text-sm mt-1">You're all caught up!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id} 
                className={`bg-[var(--m-bg-card)] border-[var(--m-border)] cursor-pointer transition-colors ${
                  !notification.is_read ? 'border-l-4 border-l-[#ffd108]' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getIcon(notification.notification_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${getTypeBadgeColor(notification.notification_type)}`}>
                          {getTypeLabel(notification.notification_type)}
                        </Badge>
                        <span className="text-[var(--m-text-muted)] text-xs">
                          {formatTime(notification.created_at)}
                        </span>
                        {!notification.is_read && (
                          <span className="w-2 h-2 rounded-full bg-[#ffd108]" />
                        )}
                      </div>
                      <h3 className={`font-medium ${notification.is_read ? 'text-[var(--m-text-secondary)]' : 'text-[var(--m-text)]'}`}>
                        {notification.title}
                      </h3>
                      <p className="text-[var(--m-text-muted)] text-sm mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {notification.job_id && (
                        <ChevronRight className="h-4 w-4 text-[var(--m-text-muted)]" />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[var(--m-text-muted)] hover:text-red-400 p-1 h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
    </MobileLayout>
  );
};

export default MobileNotifications;
